/**
 * Audio: Mistral-generated MP3s when available (preferred for clarity), browser
 * TTS as fallback. Web Audio for SFX. Question prompts are guarded so only
 * the latest requested voice can play, avoiding delayed double reads.
 */
import { resolveLocalAudioUrl } from "../data/local-assets";

// Native pitch. We used to slow this to 0.85 for ESL clarity via
// HTMLAudioElement's `preservesPitch`, but the iOS-friendly AudioBufferSource
// path doesn't preserve pitch — 0.85 made every clip sound several semitones
// lower, like a filter. Native rate is correct pitch.
const PLAYBACK_RATE = 1.0;
const MUSIC_URL = "/audio/music/Ribcage%20Rumba.mp3";
// Win track for the final-level (Battle Royale) podium — plays instead of
// the theme song, and regardless of the music toggle (it's the celebration).
const FINAL_WIN_URL = "/audio/sfx/final-level-win.mp3";
const SFX_URLS = {
  menu: "/audio/sfx/mixkit-change-menu-option-small-volume.mp3",
  finish: "/audio/sfx/mixkit-finish-race.mp3",
  correct: "/audio/sfx/mixkit-get-question-right.mp3",
  levelUp: "/audio/sfx/mixkit-level-up.mp3",
  ice: "/audio/sfx/ice.mp3",
  // Try .mp3 first (universal Safari/iOS support); fall back to .ogg if the
  // .mp3 isn't there. Drop a re-encoded ballbounce.mp3 next to the .ogg and
  // it'll be picked up automatically.
  ballBounce: ["/audio/sfx/ballbounce.mp3", "/audio/sfx/ballbounce.ogg"],
  jump: "/audio/sfx/jump-sfx.mp3",
  hit: "/audio/sfx/player-hitorbumped.mp3",
  fall: "/audio/sfx/sfx-playerfallsoff.mp3",
  timeUp: "/audio/sfx/player-runoutoftime.mp3",
  timerUrgent: "/audio/sfx/timestartwhen7secsleftintheround.mp3",
} as const;

let ctx: AudioContext | null = null;
let audioVolume = readAudioVolume();
let menuMusic: HTMLAudioElement | null = null;
let finalWinMusic: HTMLAudioElement | null = null;
let musicWanted = false;

/** Whether the player has turned theme/background music ON (default OFF).
 *  Gates menu + podium music. The final-podium win track ignores this. */
function musicAllowed(): boolean {
  try {
    const raw = localStorage.getItem("wr_game_settings_v1");
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { musicEnabled?: boolean };
    return !!parsed.musicEnabled;
  } catch {
    return false;
  }
}
let menuFadeId = 0;
let finalWinFadeId = 0;
/** Throttle: timestamp (ms) when the ice-crack SFX last played. Used to
 *  prevent the ice level from machine-gunning the same clip when many
 *  tiles crack on the same frame. */
let lastIceSfxAt = -1e9;

function readAudioVolume(): number {
  try {
    const raw = localStorage.getItem("wr_game_settings_v1");
    if (!raw) return 1;
    const parsed = JSON.parse(raw) as { audioVolume?: number };
    const n = Number(parsed.audioVolume);
    return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 1;
  } catch {
    return 1;
  }
}

function readMusicVolume(): number {
  // Music is duck-mixed under word audio. Halved from the previous ceiling
  // so the main theme sits well under speech / SFX and players can hear
  // each other during a battle. Effective max ~0.35, default ~0.2.
  try {
    const raw = localStorage.getItem("wr_game_settings_v1");
    if (!raw) return 0.2;
    const parsed = JSON.parse(raw) as { musicVolume?: number };
    const n = Number(parsed.musicVolume);
    if (!Number.isFinite(n)) return 0.2;
    return Math.max(0, Math.min(0.35, n * 0.5));
  } catch {
    return 0.2;
  }
}

function ensureMenuMusic(): HTMLAudioElement {
  if (!menuMusic) {
    menuMusic = new Audio(MUSIC_URL);
    menuMusic.loop = true;
    menuMusic.preload = "auto";
  }
  menuMusic.volume = readMusicVolume();
  return menuMusic;
}

export function refreshAudioSettings() {
  audioVolume = readAudioVolume();
  if (menuMusic) menuMusic.volume = readMusicVolume();
  if (finalWinMusic && !finalWinMusic.paused) {
    finalWinMusic.volume = Math.max(readMusicVolume(), 0.3);
  }
  // If the player just turned music OFF, fade out whatever theme is playing.
  if (!musicAllowed()) fadeOutMenuMusic(600);
}

/** Music is gated behind an explicit user action so the intro + main menu
 *  stay silent until the player presses Play. Flipping this is irreversible
 *  for the session — once enabled, normal menu music behaviour applies. */
let musicEnabledByUser = false;
export function enableMenuMusicOnUserAction() {
  unlockAudioOnUserGesture();
  // Record that the user has made a gesture (satisfies the autoplay gate)
  // even if music is currently OFF — so toggling it on later starts playback
  // immediately without needing another click.
  musicEnabledByUser = true;
  if (!musicAllowed()) return;
  startAudioWatchdog();
  startMenuMusic();
}

/**
 * Resume the SFX + voice AudioContexts on a user gesture. Browsers
 * suspend audio contexts until a real click/touch, and the in-game
 * voice prompt at the first gate was the first event that hit this
 * path — meaning all earlier SFX (countdown, pickups) were dropped.
 * Call this from any UI button the player taps before the race starts
 * (PLAY, Choose Mode buttons, in-game canvas first touch) so the audio
 * pipeline is warm by the time the race begins.
 */
export function unlockAudioOnUserGesture() {
  try {
    // SFX context.
    if (!ctx) ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    // Voice graph (separate ctx for slow-playback compressed pipeline).
    const graph = ensureVoiceGraph();
    if (graph && graph.ctx.state === "suspended") void graph.ctx.resume().catch(() => {});
  } catch { /* ignore — audio remains gated by the in-engine retry path */ }
}

export function startMenuMusic() {
  // Theme music is off unless the player has enabled it in settings.
  if (!musicAllowed()) return;
  // Honour the user-gate: don't autoplay until the user has pressed Play.
  if (!musicEnabledByUser) return;
  musicWanted = true;
  fadeInMenuMusic();
}

export function stopMenuMusic() {
  // Always fade OUT — never a hard pause. Same fade machinery as the
  // explicit fadeOutMenuMusic() for a consistent feel everywhere.
  fadeOutMenuMusic(900);
}

/** Fade the menu music in to its configured target volume. Safe to call
 *  while already playing — interrupts any previous fade and re-anchors. */
export function fadeInMenuMusic(durationMs = 900) {
  if (!musicEnabledByUser) return;
  musicWanted = true;
  const el = ensureMenuMusic();
  const target = readMusicVolume();
  if (target <= 0) return;
  const fadeId = ++menuFadeId;
  // If we're starting from a paused state, snap to silent first so the
  // ramp from 0 → target is actually audible (otherwise startVolume ==
  // target and we'd hear no fade at all).
  if (el.paused) el.volume = 0;
  const startVolume = el.volume;
  const startedAt = performance.now();
  // Kick playback off immediately if it's not already; ramp the volume in
  // parallel. On browsers that block autoplay we register a one-shot
  // gesture listener to retry.
  if (el.paused) {
    el.play().catch(() => {
      const unlock = () => {
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
        if (musicWanted) void ensureMenuMusic().play().catch(() => {});
      };
      window.addEventListener("pointerdown", unlock, { once: true });
      window.addEventListener("keydown", unlock, { once: true });
    });
  }
  const step = () => {
    if (fadeId !== menuFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    el.volume = startVolume + (target - startVolume) * easeOutCubic(t);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** Podium theme music — reuses the menu-music <audio> element + fade
 *  machinery so we never end up with two overlapping music tracks. Gated by
 *  the music setting (off by default). The final-level podium uses
 *  {@link startFinalWinMusic} instead. */
export function startPodiumMusic() {
  if (!musicAllowed()) return;
  musicWanted = true;
  startAudioWatchdog();
  fadeInPodiumMusic();
}

/** Final-level (Battle Royale) podium win track. Plays `final-level-win.mp3`
 *  instead of the theme song, ALWAYS (ignores the music toggle) — it's the
 *  reward for winning the last level. Loops + fades, on its own element so it
 *  never collides with the menu/podium theme. Volume tracks the music slider
 *  (with an audible floor). */
export function startFinalWinMusic() {
  if (!finalWinMusic) {
    finalWinMusic = new Audio(FINAL_WIN_URL);
    finalWinMusic.loop = true;
    finalWinMusic.preload = "auto";
  }
  const el = finalWinMusic;
  startAudioWatchdog();
  const target = Math.max(readMusicVolume(), 0.3); // floor so it's audible even at low music vol
  el.volume = 0;
  const fadeId = ++finalWinFadeId;
  const startedAt = performance.now();
  el.play().catch(() => {
    const unlock = () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      void el.play().catch(() => {});
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  });
  const step = () => {
    if (fadeId !== finalWinFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / 600);
    el.volume = target * easeOutCubic(t);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

export function stopFinalWinMusic(durationMs = 1200) {
  const el = finalWinMusic;
  if (!el || el.paused) return;
  const fadeId = ++finalWinFadeId;
  const startVolume = el.volume;
  const startedAt = performance.now();
  const step = () => {
    if (fadeId !== finalWinFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    el.volume = startVolume * (1 - easeOutCubic(t));
    if (t < 1) { requestAnimationFrame(step); return; }
    el.pause();
    el.currentTime = 0;
  };
  requestAnimationFrame(step);
}

function fadeInPodiumMusic(durationMs = 900) {
  const el = ensureMenuMusic();
  const target = Math.max(readMusicVolume(), 0.22); // floor so it's actually audible
  const fadeId = ++menuFadeId;
  if (el.paused) el.volume = 0;
  const startVolume = el.volume;
  const startedAt = performance.now();
  if (el.paused) {
    el.play().catch(() => {
      const unlock = () => {
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
        if (musicWanted) void ensureMenuMusic().play().catch(() => {});
      };
      window.addEventListener("pointerdown", unlock, { once: true });
      window.addEventListener("keydown", unlock, { once: true });
    });
  }
  const step = () => {
    if (fadeId !== menuFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    el.volume = startVolume + (target - startVolume) * easeOutCubic(t);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** Fade the podium music out over `durationMs` (default 1800ms — slow
 *  enough to feel like a proper outro). Pauses the element when silent. */
export function fadeOutPodiumMusic(durationMs = 1800) {
  musicWanted = false;
  if (!menuMusic || menuMusic.paused) return;
  const el = menuMusic;
  const fadeId = ++menuFadeId;
  const startVolume = el.volume;
  const startedAt = performance.now();
  const step = () => {
    if (fadeId !== menuFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    el.volume = startVolume * (1 - easeOutCubic(t));
    if (t < 1) { requestAnimationFrame(step); return; }
    el.pause();
    el.currentTime = 0;
    el.volume = readMusicVolume();
  };
  requestAnimationFrame(step);
}

export function fadeOutMenuMusic(durationMs = 1200) {
  musicWanted = false;
  if (!menuMusic || menuMusic.paused) return;
  const el = menuMusic;
  const fadeId = ++menuFadeId;
  const startVolume = el.volume;
  const startedAt = performance.now();
  const step = () => {
    if (fadeId !== menuFadeId) return;
    const t = Math.min(1, (performance.now() - startedAt) / durationMs);
    el.volume = startVolume * (1 - easeOutCubic(t));
    if (t < 1) {
      requestAnimationFrame(step);
      return;
    }
    el.pause();
    el.currentTime = 0;
    el.volume = readMusicVolume(); // restore so the next play() starts at the right level
  };
  requestAnimationFrame(step);
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function audio(): AudioContext {
  if (!ctx) ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/**
 * Audio watchdog. Some browsers (iOS Safari especially) randomly suspend the
 * AudioContext or pause looping <audio> elements when a tab backgrounds,
 * the screen locks, or another audio source briefly takes focus. Without a
 * recovery path the game just goes silent for the rest of the session.
 *
 * Strategy: every 2s, if the context is suspended → resume; if menu music
 * is "wanted" but the <audio> is paused / ended → restart it. Also rehook on
 * visibility/focus changes for an immediate recovery.
 */
let _watchdogStarted = false;
function startAudioWatchdog() {
  if (_watchdogStarted || typeof window === "undefined") return;
  _watchdogStarted = true;
  const recover = () => {
    if (ctx && ctx.state === "suspended") {
      void ctx.resume().catch(() => {});
    }
    if (musicWanted && menuMusic && (menuMusic.paused || menuMusic.ended)) {
      try {
        menuMusic.currentTime = menuMusic.currentTime || 0;
        void menuMusic.play().catch(() => {});
      } catch {
        /* ignore — will retry next tick */
      }
    }
  };
  window.setInterval(recover, 2000);
  document.addEventListener("visibilitychange", recover);
  window.addEventListener("focus", recover);
  window.addEventListener("pageshow", recover);
}

function tone(
  freq: number,
  durMs: number,
  type: OscillatorType = "sine",
  gain = 0.15,
  delayMs = 0,
) {
  const a = audio();
  const o = a.createOscillator();
  const g = a.createGain();
  const start = a.currentTime + delayMs / 1000;
  const end = start + durMs / 1000;
  o.type = type;
  o.frequency.setValueAtTime(freq, start);
  g.gain.setValueAtTime(gain * audioVolume, start);
  g.gain.exponentialRampToValueAtTime(0.0001, end);
  o.connect(g).connect(a.destination);
  o.start(start);
  o.stop(end);
}

/**
 * Pool of pre-loaded <audio> elements, indexed by URL. Each slot is a
 * round-robin so rapid-fire calls (e.g. hit, jump) can overlap without
 * waiting for the previous play to end. The cache also means iOS doesn't
 * have to re-load the MP3 on every shot, which was producing a noticeable
 * "first hit is silent" problem with `new Audio(...)`.
 */
const clipPool = new Map<string, HTMLAudioElement[]>();
const CLIP_POOL_SIZE = 3;
function getPoolEl(url: string): HTMLAudioElement {
  let pool = clipPool.get(url);
  if (!pool) {
    pool = [];
    for (let i = 0; i < CLIP_POOL_SIZE; i++) {
      const el = new Audio(url);
      el.preload = "auto";
      pool.push(el);
    }
    clipPool.set(url, pool);
  }
  // Prefer an element that's finished playing; otherwise reuse oldest.
  let pick = pool.find((e) => e.paused || e.ended);
  if (!pick) {
    pick = pool[0]!;
    pool.push(pool.shift()!);
  }
  return pick;
}

function playClip(url: string | readonly string[], volumeScale = 1): boolean {
  const urls = Array.isArray(url) ? url : [url];
  // Pick the first URL whose extension the browser claims to support.
  // canPlayType returns "probably", "maybe", or "" — anything non-empty
  // means the browser will at least try the format.
  const probe = document.createElement("audio");
  const pick = urls.find((u) => {
    const ext = u.split(".").pop()?.toLowerCase();
    if (ext === "mp3") return !!probe.canPlayType("audio/mpeg");
    if (ext === "ogg") return !!probe.canPlayType('audio/ogg; codecs="vorbis"');
    if (ext === "wav") return !!probe.canPlayType("audio/wav");
    if (ext === "m4a" || ext === "aac") return !!probe.canPlayType("audio/mp4");
    return true;
  }) ?? urls[0];
  if (!pick) return false;
  try {
    const el = getPoolEl(pick);
    el.volume = Math.max(0, Math.min(1, audioVolume * volumeScale));
    try { el.currentTime = 0; } catch { /* iOS can throw if not loaded yet */ }
    void el.play().catch(() => {});
    return true;
  } catch {
    return false;
  }
}

export const sfx = {
  coin: () => tone(880, 90, "sine", 0.2),
  hit: () => {
    if (playClip(SFX_URLS.hit, 0.7)) return;
    tone(140, 250, "sawtooth", 0.25);
  },
  /** Player jumped — light pop. */
  jump: () => {
    playClip(SFX_URLS.jump, 0.55);
  },
  /** Player fell off the platform — used by Level 10 survival. */
  fall: () => {
    playClip(SFX_URLS.fall, 0.85);
  },
  /** Round timer hit zero. */
  timeUp: () => {
    playClip(SFX_URLS.timeUp, 0.9);
  },
  /** Urgent countdown tick — fires once when 7 seconds remain. */
  timerUrgent: () => {
    playClip(SFX_URLS.timerUrgent, 0.85);
  },
  correct: () => {
    // Volume halved (0.54 → 0.27) — the chime was overpowering the
    // streak banner + Mistral pronunciation that follows. Tone fallback
    // also halved so the synth path matches.
    if (playClip(SFX_URLS.correct, 0.27)) return;
    tone(660, 100, "sine", 0.075);
    setTimeout(() => tone(880, 140, "sine", 0.075), 90);
  },
  wrong: () => {
    tone(220, 180, "square", 0.2);
    setTimeout(() => tone(180, 220, "square", 0.2), 160);
  },
  finish: () => {
    if (playClip(SFX_URLS.finish, 0.9)) return;
    tone(523, 130);
    setTimeout(() => tone(659, 130), 120);
    setTimeout(() => tone(784, 200), 240);
  },
  menu: () => {
    // Disabled — the mixkit menu-click sample was annoying on every
    // hover/select. Left as a no-op so callers don't need to change.
  },
  levelUp: () => {
    if (playClip(SFX_URLS.levelUp, 0.85)) return;
    tone(523, 100);
    setTimeout(() => tone(784, 130), 100);
    setTimeout(() => tone(1046, 180), 220);
  },
  /** Ice crack — gated so at most one crack plays at a time (was
   *  spamming whenever multiple tiles cracked the same frame) and
   *  with a long cool-down + low volume so it sits in the background
   *  instead of overpowering the rest of the audio. */
  ice: (volumeScale = 0.9) => {
    const now = performance.now();
    if (now - lastIceSfxAt < 320) return; // de-spam: 1 per ~third of a sec
    lastIceSfxAt = now;
    playClip(SFX_URLS.ice, Math.min(0.18, volumeScale * 0.12));
  },
  /** Ball bouncing on the ground. volumeScale 0..1 lets the caller fade
   *  distant bounces (proximity-based loudness). */
  ballBounce: (volumeScale = 0.85) => {
    playClip(SFX_URLS.ballBounce, volumeScale);
  },
  countdown: (label: string) => {
    if (label === "GO!") {
      tone(880, 360, "square", 0.22);
      tone(1760, 260, "sine", 0.08);
      tone(660, 90, "triangle", 0.12, 70);
      return;
    }
    tone(220, 170, "square", 0.2);
    tone(110, 170, "sawtooth", 0.06);
  },
};

let currentAudio: HTMLAudioElement | null = null;
let voiceSerial = 0;
let voiceUnlockQueued = false;

// ─── Voice audio routing ───────────────────────────────────────────────────
//
// iOS Safari handles rebuilt Web Audio graphs poorly: per-play creation of
// MediaElementSource + compressors + limiters introduced clicks, gaps, and
// dropouts on iPad/iPhone. Switching to:
//   - AudioBuffer cache (decodeAudioData once, replay for free)
//   - One persistent voice graph: comp → gain → limiter → destination
//   - One-shot AudioBufferSourceNode per play (cheap, GC'd on `onended`)
// HTMLAudioElement path stays as a fallback only when decode hasn't finished.

const voiceBufferCache = new Map<string, Promise<AudioBuffer | null>>();
let voiceGraphCtx: AudioContext | null = null;
let voiceGraphInput: AudioNode | null = null;
let currentVoiceSource: AudioBufferSourceNode | null = null;

function ensureVoiceGraph(): { ctx: AudioContext; input: AudioNode } | null {
  try {
    const ctx = audio();
    if (voiceGraphCtx === ctx && voiceGraphInput) return { ctx, input: voiceGraphInput };
    // Single mild compressor + gain. The previous chain (compressor → gain →
    // limiter) caused audible pumping / dropouts on iOS Safari + Chrome:
    // the limiter's fast attack at -6 dB was modulating the signal in a way
    // that iOS's audio engine renders as breathing artifacts. A single soft
    // compressor with conservative settings keeps loudness consistent
    // without the artifacts.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -24;   // higher = compresses less aggressively
    comp.knee.value = 14;         // soft knee, smoother onset
    comp.ratio.value = 3;         // gentle 3:1
    comp.attack.value = 0.012;    // slower attack avoids pumping
    comp.release.value = 0.25;
    const gain = ctx.createGain();
    gain.gain.value = 1.9 * audioVolume;
    comp.connect(gain).connect(ctx.destination);
    voiceGraphCtx = ctx;
    voiceGraphInput = comp;
    return { ctx, input: comp };
  } catch {
    return null;
  }
}

async function loadVoiceBuffer(url: string): Promise<AudioBuffer | null> {
  const existing = voiceBufferCache.get(url);
  if (existing) return existing;
  const promise = (async () => {
    const graph = ensureVoiceGraph();
    if (!graph) return null;
    try {
      const res = await fetch(url, { cache: "force-cache" });
      if (!res.ok) return null;
      const arr = await res.arrayBuffer();
      // Safari requires the callback form on older versions; modern is promise.
      return await new Promise<AudioBuffer | null>((resolve) => {
        try {
          graph.ctx.decodeAudioData(
            arr,
            (buf) => resolve(buf),
            () => resolve(null),
          );
        } catch {
          resolve(null);
        }
      });
    } catch {
      return null;
    }
  })();
  voiceBufferCache.set(url, promise);
  return promise;
}

function stopCurrentVoiceSource() {
  if (currentVoiceSource) {
    try { currentVoiceSource.stop(); } catch { /* already stopped */ }
    currentVoiceSource = null;
  }
}

function stopCurrent() {
  stopCurrentVoiceSource();
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio = null;
  }
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}

function speakWithBrowser(text: string, lang: string) {
  if (!text) return;
  if (!("speechSynthesis" in window)) return;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  u.rate = 0.95;
  u.volume = audioVolume;
  // Explicitly pick a voice that matches the requested lang prefix when
  // available — otherwise some browsers fall back to the default English
  // voice and read Vietnamese text with an English accent.
  try {
    const voices = window.speechSynthesis.getVoices();
    const prefix = lang.slice(0, 2).toLowerCase();
    const match = voices.find((v) => v.lang.toLowerCase().startsWith(prefix));
    if (match) u.voice = match;
  } catch { /* getVoices() can throw on older Safari before voices load */ }
  window.speechSynthesis.speak(u);
}

function queueVoiceUnlockRetry(fn: () => void) {
  if (voiceUnlockQueued) return;
  voiceUnlockQueued = true;
  const unlock = () => {
    voiceUnlockQueued = false;
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
    fn();
  };
  window.addEventListener("pointerdown", unlock, { once: true });
  window.addEventListener("keydown", unlock, { once: true });
}

function playVoiceAudio(
  text: string,
  audioUrl: string,
  lang: string,
  serial: number,
  allowGestureRetry: boolean,
  allowBrowserFallback: boolean,
) {
  // Fallback hooks (defined first so the buffer-path async block can close
  // over them safely).
  let fellBack = false;
  const fallbackOnce = () => {
    if (fellBack || serial !== voiceSerial) return;
    fellBack = true;
    if (allowBrowserFallback) speakWithBrowser(text, lang);
  };

  // Preferred path: AudioBuffer through the persistent voice graph. Avoids
  // recreating Web Audio nodes per play (the root cause of the iOS dropouts
  // / cracking sound). Falls back to HTMLAudioElement when decode hasn't
  // landed yet on the first reference to a URL.
  const graph = ensureVoiceGraph();
  if (graph) {
    void (async () => {
      try {
        const buf = await loadVoiceBuffer(audioUrl);
        // Stale playback request — newer audio has been queued; bail.
        if (serial !== voiceSerial) return;
        if (!buf) {
          fallbackOnce();
          return;
        }
        if (graph.ctx.state === "suspended") {
          await graph.ctx.resume().catch(() => {});
        }
        stopCurrentVoiceSource();
        const src = graph.ctx.createBufferSource();
        src.buffer = buf;
        src.playbackRate.value = PLAYBACK_RATE;
        src.connect(graph.input);
        src.onended = () => {
          if (currentVoiceSource === src) currentVoiceSource = null;
        };
        try {
          src.start();
          currentVoiceSource = src;
        } catch {
          fallbackOnce();
        }
      } catch {
        fallbackOnce();
      }
    })();
    return; // buffer path handles play + fallback above.
  }
  const el = new Audio(audioUrl);
  el.preload = "auto";
  el.playbackRate = PLAYBACK_RATE;
  el.preservesPitch = true;
  el.volume = Math.min(1, audioVolume);
  currentAudio = el;
  el.onerror = fallbackOnce;
  el.onended = () => {
    if (currentAudio === el) currentAudio = null;
  };
  el.play().catch(() => {
    if (serial !== voiceSerial) return;
    // Speak via browser TTS right away so the learner hears *something*
    // even if MP3 autoplay is blocked. A pending gesture retry will then
    // upgrade to the MP3 on next click/keydown.
    fallbackOnce();
    if (allowGestureRetry) {
      queueVoiceUnlockRetry(() => {
        if (serial === voiceSerial) playVoiceAudio(text, audioUrl, lang, serial, false, allowBrowserFallback);
      });
    }
  });
}

/**
 * Speak `text`. If `audioUrl` is provided, plays that MP3 (slowed via
 * playbackRate). Otherwise falls back to browser TTS.
 */
/** Read the player's chosen learn-language from localStorage and return the
 *  matching BCP-47 tag for browser TTS. Falls back to en-US. */
function getDefaultLang(): string {
  try {
    const raw = localStorage.getItem("wr_profile_v1");
    if (!raw) return "en-US";
    const p = JSON.parse(raw) as { learnLanguage?: string };
    if (p.learnLanguage === "vi") return "vi-VN";
  } catch {
    /* ignore */
  }
  return "en-US";
}

export function speak(text: string, opts: { audioUrl?: string | null; lang?: string } = {}) {
  const { audioUrl, lang = getDefaultLang() } = opts;
  const serial = ++voiceSerial;
  speakForSerial(text, audioUrl, lang, serial);
}

/**
 * Epic "BATTLE ROYALE" announcement for the survival arena intro. Plays the
 * Mistral-generated MP3 through a custom chain: pitched down for a booming
 * voice, plus a feedback delay for the stadium-echo effect. Falls back to
 * browser TTS if the MP3 isn't present.
 */
export function playBattleRoyaleAnnounce() {
  const url = resolveLocalAudioUrl("/audio/tts/battle-royale.mp3");
  const graph = ensureVoiceGraph();
  if (!graph || !url) {
    speakWithBrowser("Battle Royale", "en-US");
    return;
  }
  void (async () => {
    const buf = await loadVoiceBuffer(url);
    if (!buf) {
      speakWithBrowser("Battle Royale", "en-US");
      return;
    }
    const { ctx } = graph;
    if (ctx.state === "suspended") await ctx.resume().catch(() => {});
    const vol = Math.min(1, audioVolume);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 1.22;            // faster + higher → excited shout

    const out = ctx.createGain();
    out.gain.value = 1.5 * vol;
    const dry = ctx.createGain();
    dry.gain.value = 1.0;
    // Tight slap-back echo — punchy hype, not a cavernous wash.
    const delay = ctx.createDelay(1.0);
    delay.delayTime.value = 0.14;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.28;
    const wet = ctx.createGain();
    wet.gain.value = 0.4;

    src.connect(dry).connect(out);
    src.connect(delay);
    delay.connect(feedback).connect(delay); // recirculating tail
    delay.connect(wet).connect(out);
    out.connect(ctx.destination);

    try {
      src.start();
    } catch {
      speakWithBrowser("Battle Royale", "en-US");
    }
  })();
}

function speakForSerial(text: string, audioUrl: string | null | undefined, lang: string, serial: number) {
  stopCurrent();
  const localAudioUrl = resolveLocalAudioUrl(audioUrl);
  if (localAudioUrl) {
    // allowBrowserFallback=true → if autoplay is blocked we still get TTS,
    // instead of silently waiting for a user gesture that may never come.
    playVoiceAudio(text, localAudioUrl, lang, serial, true, true);
    return;
  }
  speakWithBrowser(text, lang);
}

export interface LoopHandle {
  cancel: () => void;
}

export function speakPromptOnce(
  text: string,
  opts: { audioUrl?: string | null; lang?: string } = {},
): LoopHandle {
  const loopSerial = ++voiceSerial;
  let cancelled = false;
  let timer = 0;
  const tick = () => {
    if (cancelled || loopSerial !== voiceSerial) return;
    speakForSerial(text, opts.audioUrl, opts.lang ?? getDefaultLang(), loopSerial);
    timer = window.setTimeout(tick, 4300);
  };
  // Defer first tick ~120ms so React StrictMode double-mount doesn't kill it.
  timer = window.setTimeout(tick, 120);
  return {
    cancel: () => {
      cancelled = true;
      window.clearTimeout(timer);
      stopCurrent();
    },
  };
}
